import { t } from '@hefe/i18n';
import {
  RequestBasePostClubTask,
  RequestBaseUpdateTask,
  Task,
} from '@hefe/models';
import { ModalsSlice, TasksActions } from '@hefe/store';
import { useState } from 'react';
import { useAppDispatch } from '../../../../store/store';
import CustomForm from '../../../core/components/form/custom-form';

interface AddEditClubTaskFormProps {
  task?: Task;
  showSaveButton?: boolean;
}

export default function AddEditClubTaskForm(props: AddEditClubTaskFormProps) {
  const { task } = props;

  const dispatch = useAppDispatch();

  const [saving, setSaving] = useState(false);

  const formKey = `add-edit-club-task-form-${task ? task.id : 'new'}`;

  const onSubmit = (values: any, resetForm: any) => {
    const setLoading = ModalsSlice.modalsActions.setModalLoading;
    const close = ModalsSlice.modalsActions.closeModal;
    dispatch(setLoading(true));
    setSaving(true);

    if (props.task) {
      const updatedTask: RequestBaseUpdateTask = {
        name: values.name,
        description: values.description,
        at_home_games: values.at_home_games ?? false,
        at_away_games: values.at_away_games ?? false,
        at_practices: values.at_practices ?? false,
        min_num_of_users: values.min_num_of_users,
        max_num_of_users: values.max_num_of_users,
        is_public: values.is_public,
        requires_presence: values.requires_presence ?? false,
        allow_own_assignments: values.allow_own_assignments ?? false,
      };

      dispatch(
        TasksActions.updateTask({
          taskId: props.task.id,
          request: updatedTask,
        })
      )
        .unwrap()
        .then(() => {
          resetForm();
          dispatch(close());
        })
        .catch(() => null)
        .finally(() => {
          setSaving(false);
          dispatch(setLoading(false));
        });
    } else {
      const submitData: RequestBasePostClubTask = {
        name: values.name,
        description: values.description || '',
        at_home_games: values.at_home_games ?? false,
        at_away_games: values.at_away_games ?? false,
        at_practices: values.at_practices ?? false,
        min_num_of_users: values.min_num_of_users,
        max_num_of_users: values.max_num_of_users,
        is_public: values.is_public,
        requires_presence: values.requires_presence ?? false,
        allow_own_assignments: values.allow_own_assignments ?? false,
      };

      dispatch(TasksActions.createClubTask(submitData))
        .unwrap()
        .then(() => {
          resetForm();
          dispatch(close());
        })
        .catch(() => null)
        .finally(() => {
          setSaving(false);
          dispatch(setLoading(false));
        });
    }
  };

  return (
    <CustomForm
      key={formKey}
      formId={!props.showSaveButton ? formKey : undefined}
      loading={saving}
      fields={[
        {
          name: 'name',
          label: t('name'),
          type: 'text',
          initalValue: task?.name,
          rules: [
            {
              required: true,
              message: t('form_errors.name'),
            },
          ],
        },
        {
          name: 'at_home_games',
          label: t('at_home_games') + '?',
          type: 'checkbox',
          initalValue: task?.at_home_games,
          rules: [],
        },
        {
          name: 'at_away_games',
          label: t('at_away_games') + '?',
          type: 'checkbox',
          initalValue: task?.at_away_games,
          rules: [],
        },
        {
          name: 'at_practices',
          label: t('at_practices') + '?',
          type: 'checkbox',
          initalValue: task?.at_practices,
          rules: [],
        },
        {
          name: 'min_num_of_users',
          label: t('min_num_of_users') + '?',
          type: 'number',
          initalValue: task?.min_num_of_users,
          rules: [],
        },
        {
          name: 'max_num_of_users',
          label: t('max_num_of_users') + '?',
          type: 'number',
          initalValue: task?.max_num_of_users,
          rules: [],
        },
        {
          name: 'is_public',
          label: t('is_public') + '?',
          type: 'checkbox',
          initalValue: task?.is_public,
          rules: [],
          extraLabel: t('information_texts.is_public'),
        },
        {
          name: 'requires_presence',
          label: t('tasks.requires_presence.title') + '?',
          extraLabel: t('tasks.requires_presence.description'),
          type: 'checkbox',
          initalValue: task?.requires_presence,
          rules: [],
        },
        {
          name: 'allow_own_assignments',
          label: t('tasks.own_assignments.title'),
          extraLabel: t('tasks.own_assignments.allow') + '?',
          type: 'checkbox',
          initalValue: task?.allow_own_assignments,
          rules: [],
        },
        {
          name: 'description',
          label: t('note'),
          type: 'text',
          initalValue: task?.description,
          rules: [],
        },
      ]}
      onSubmit={onSubmit}
    />
  );
}
